import useObservableValue from "../../../hooks/useObservableValue";
import copilotState$, { initialCopilotState } from "../observables/copilotState$";
import {
  COPILOT_BROADCAST,
  COPILOT_BROADCAST_STATUS,
  COPILOT_RECOMMENDATION_STATUS,
  COPILOT_STATE,
  COPILOT_SUGGESTION,
} from "../types/CommonTypes";

function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function useCopilotValue(): COPILOT_STATE {
  return useObservableValue(copilotState$);
}

export default useCopilotValue;

export const getCopilotValue = () => copilotState$.getValue();

export const setCopilotValue = (state: COPILOT_STATE) => {
  copilotState$.next(state);
};

export const updateCopilotValue = (partial: Partial<COPILOT_STATE>) => {
  copilotState$.next({ ...copilotState$.getValue(), ...partial });
};

// Module-level draft persisted across CopilotPanel unmount/remount cycles.
// Kept here so resetCopilotValue clears it on logout.
let _copilotDraft = "";
export const getCopilotDraft = () => _copilotDraft;
export const setCopilotDraft = (text: string) => {
  _copilotDraft = text;
};
export const clearCopilotDraft = () => {
  _copilotDraft = "";
};

export const resetCopilotValue = () => {
  clearCopilotDraft();
  setCopilotValue(initialCopilotState);
};

export function ensureCopilotSession() {
  const cur = getCopilotValue();
  if (cur.sessionId) return;
  updateCopilotValue({ sessionId: generateUUID() });
}

export function appendCopilotUserMessage(text: string) {
  const trimmed = text.trim();
  if (!trimmed) return;
  const cur = getCopilotValue();
  const id = `msg_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 9)}`;
  updateCopilotValue({
    messages: [
      ...cur.messages,
      { id, role: "user", text: trimmed, createdAt: Date.now() },
    ],
    isAwaitingResponse: true,
  });
}

export function appendCopilotAssistantMessage(
  text: string,
  tags?: COPILOT_STATE["messages"][number]["tags"],
  recommendation?: COPILOT_STATE["messages"][number]["recommendation"]
): string {
  const cur = getCopilotValue();
  const id = `msg_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 9)}`;
  updateCopilotValue({
    messages: [
      ...cur.messages,
      { id, role: "assistant", text, tags, recommendation, createdAt: Date.now() },
    ],
    isAwaitingResponse: false,
  });
  return id;
}

export function setPendingSuggestions(suggestions: COPILOT_SUGGESTION[]) {
  updateCopilotValue({ pendingSuggestions: suggestions });
}

export function updateCopilotRecommendationStatus(
  messageId: string,
  status: COPILOT_RECOMMENDATION_STATUS,
  successMessage?: string
) {
  const cur = getCopilotValue();
  updateCopilotValue({
    messages: cur.messages.map((m) =>
      m.id === messageId && m.recommendation
        ? { ...m, recommendation: { ...m.recommendation, status, successMessage } }
        : m
    ),
  });
}

export function appendCopilotBroadcastMessage(broadcast: COPILOT_BROADCAST): void {
  const cur = getCopilotValue();
  const id = `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`; // NOSONAR
  updateCopilotValue({
    messages: [
      ...cur.messages,
      { id, role: "assistant", text: "", broadcast, createdAt: Date.now() },
    ],
    isAwaitingResponse: false,
  });
}

export function updateCopilotBroadcastStatus(
  messageId: string,
  status: COPILOT_BROADCAST_STATUS,
  extra?: Partial<Pick<COPILOT_BROADCAST, "executingCount" | "successCount" | "successSentAt" | "successRecipients">>
) {
  const cur = getCopilotValue();
  updateCopilotValue({
    messages: cur.messages.map((m) =>
      m.id === messageId && m.broadcast
        ? { ...m, broadcast: { ...m.broadcast, status, ...extra } }
        : m
    ),
  });
}

export function startNewCopilotSession() {
  updateCopilotValue({
    sessionId: generateUUID(),
    messages: [],
    isAwaitingResponse: false,
    pendingSuggestions: [],
  });
}
